import React, { useEffect, useMemo, useState } from "react";
import AdminPageHeader from "../../components/AdminPageHeader";
import useUserRole from "../../hooks/useUserRole";
import useRolePermissions from "../../hooks/useRolePermissions";
import {
  collection,
  collectionGroup,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { db, resolvePayrollForPunch } from "../../lib/firebase";

function getYearStart(year) {
  return new Date(year, 0, 1, 0, 0, 0, 0);
}

function getYearEnd(year) {
  return new Date(year, 11, 31, 23, 59, 59, 999);
}

function toDate(ts) {
  if (!ts) return null;
  if (typeof ts?.toDate === "function") return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

function cleanText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function smartCase(value = "") {
  return cleanText(value)
    .split(" ")
    .filter(Boolean)
    .map((word) => {
      if (!word) return word;

      if (word.includes("-")) {
        return word
          .split("-")
          .map((part) =>
            part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part
          )
          .join("-");
      }

      if (word.includes("'")) {
        return word
          .split("'")
          .map((part) =>
            part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part
          )
          .join("'");
      }

      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function splitName(user = {}) {
  const first = cleanText(
    user.firstName ||
      user.givenName ||
      user.applicationData?.firstName ||
      ""
  );

  const last = cleanText(
    user.lastName ||
      user.familyName ||
      user.applicationData?.lastName ||
      ""
  );

  if (first || last) {
    return {
      firstName: smartCase(first),
      lastName: smartCase(last),
    };
  }

  const display = cleanText(
    user.displayName ||
      user.name ||
      user.applicationData?.name ||
      ""
  );

  if (!display) {
    return { firstName: "", lastName: "" };
  }

  const parts = display.split(" ").filter(Boolean);

  if (parts.length === 1) {
    return {
      firstName: smartCase(parts[0]),
      lastName: "",
    };
  }

  return {
    firstName: smartCase(parts.slice(0, -1).join(" ")),
    lastName: smartCase(parts.slice(-1).join(" ")),
  };
}

function parseFullAddress(full = "") {
  const value = cleanText(full);
  if (!value) return { street: "", city: "", state: "", zip: "" };

  // Normalize commas
  const normalized = value.replace(/\s*,\s*/g, ", ");

  // 1) Street, City, ST ZIP
  let match = normalized.match(/^(.*?),\s*(.*?),\s*([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (match) {
    return {
      street: smartCase(match[1]),
      city: smartCase(match[2]),
      state: match[3].toUpperCase(),
      zip: match[4],
    };
  }

  // 2) Street, City ST ZIP
  match = normalized.match(/^(.*?),\s*(.*?)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (match) {
    return {
      street: smartCase(match[1]),
      city: smartCase(match[2]),
      state: match[3].toUpperCase(),
      zip: match[4],
    };
  }

  // 3) No commas: try ".... City ST ZIP"
  match = normalized.match(/^(.*)\s+([A-Za-z][A-Za-z\s.'-]+)\s+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (match) {
    const beforeCity = cleanText(match[1]);
    const city = smartCase(match[2]);
    const state = match[3].toUpperCase();
    const zip = match[4];

    // Try to split street from city by common street suffix
    const streetSuffixes = [
      "St", "Street", "Rd", "Road", "Ave", "Avenue", "Dr", "Drive",
      "Ln", "Lane", "Ct", "Court", "Blvd", "Boulevard", "Pl", "Place",
      "Ter", "Terrace", "Way", "Cir", "Circle", "Pkwy", "Parkway"
    ];

    const streetWords = beforeCity.split(" ");
    let splitIndex = -1;

    for (let i = 0; i < streetWords.length; i++) {
      const w = streetWords[i].replace(/[.,]/g, "");
      if (streetSuffixes.includes(w)) {
        splitIndex = i;
      }
    }

    if (splitIndex >= 0) {
      const street = smartCase(streetWords.slice(0, splitIndex + 1).join(" "));
      const possibleCityPrefix = cleanText(streetWords.slice(splitIndex + 1).join(" "));
      return {
        street,
        city: smartCase(`${possibleCityPrefix} ${city}`.trim()),
        state,
        zip,
      };
    }

    return {
      street: smartCase(beforeCity),
      city,
      state,
      zip,
    };
  }

  return {
    street: smartCase(normalized),
    city: "",
    state: "",
    zip: "",
  };
}

function getAddressParts(user = {}) {
  const rawStreet = cleanText(
    user.street ||
      user.streetAddress ||
      user.addressLine1 ||
      user.homeAddress ||
      user.mailingAddress ||
      user.address ||
      user.applicationData?.street ||
      user.applicationData?.address ||
      ""
  );

  const parsed = parseFullAddress(rawStreet);

  const city = cleanText(
    user.city ||
      user.applicationData?.city ||
      parsed.city ||
      ""
  );

  const state = cleanText(
    user.state ||
      user.applicationData?.state ||
      parsed.state ||
      ""
  );

  const zip = cleanText(
    user.zip ||
      user.zipCode ||
      user.postalCode ||
      user.applicationData?.zip ||
      user.applicationData?.zipCode ||
      parsed.zip ||
      ""
  );

  return {
    street: parsed.street || smartCase(rawStreet),
    city: city ? smartCase(city) : "",
    state: state ? state.toUpperCase() : "",
    zip,
  };
}

function getPosition(user = {}) {
  return cleanText(user.position || user.applicationData?.position || "");
}

function getBasePosition(user = {}) {
  return cleanText(
    user.basePosition ||
      user.providerLevel ||
      user.position ||
      user.applicationData?.basePosition ||
      user.applicationData?.position ||
      ""
  );
}

function getEmpId(user = {}) {
  return String(
    user.employeeNumber ||
      user.empNumber ||
      user.employeeId ||
      user.empId ||
      ""
  ).trim();
}

function getEmail(user = {}) {
  return String(
    user.email ||
      user.emailKey ||
      user.authEmail ||
      user.userEmail ||
      user.applicationData?.email ||
      ""
  ).trim();
}

function isTrinityEmployee(user = {}) {
  const thEmployeeStatus = String(
    user.thEmployeeStatus ||
      user.applicationData?.thEmployeeStatus ||
      user.thEmployee ||
      user.applicationData?.thEmployee ||
      ""
  ).trim().toLowerCase();

  const isTHEmployee =
    user.isTHEmployee ??
    user.applicationData?.isTHEmployee ??
    null;

  const payTitle = String(
    user.payTitle || user.applicationData?.payTitle || ""
  ).trim();

  const position = getPosition(user);
  const basePosition = getBasePosition(user);

  const empId = getEmpId(user);

  if (isTHEmployee === true) return true;
  if (thEmployeeStatus === "yes" || thEmployeeStatus === "true") return true;

  if (
    payTitle === "THONE Emp" ||
    position === "THONE Emp" ||
    basePosition === "THONE Emp"
  ) {
    return true;
  }

  if (empId) return true;

  return false;
}

function buildBaseRows(users = []) {
  return users
    .map((user) => {
      const uid = String(user.uid || user.id || "").trim();
      const { firstName, lastName } = splitName(user);
      const addr = getAddressParts(user);
      const position = getPosition(user);
      const level = getBasePosition(user) || position;
      const thEmp = isTrinityEmployee(user);

      return {
        uid,
        firstName,
        lastName,
        email: getEmail(user),
        street: addr.street,
        city: addr.city,
        state: addr.state,
        zip: addr.zip,
        thEmp,
        empId: getEmpId(user),
        level,
        position,
        role: String(user.role || "").trim(),
        rateDisplay: "",
        hours: 0,
        pay: 0,
      };
    })
    .sort((a, b) => {
      const lastCmp = String(a.lastName || "").localeCompare(String(b.lastName || ""));
      if (lastCmp !== 0) return lastCmp;
      return String(a.firstName || "").localeCompare(String(b.firstName || ""));
    });
}

function buildExportRows(rows) {
  return rows.map((row) => ({
    "Last Name": row.lastName,
    "First Name": row.firstName,
    "E-Mail Address": row.email,
    "Supplier ID #": "",
    Street: row.street,
    City: row.city,
    State: row.state,
    Zip: row.zip,
    "TH Emp?": row.thEmp ? "Yes" : "No",
    "Emp ID": row.empId,
    W9: "",
    Level: row.level,
    Rate: row.rateDisplay,
    Hours: Number(row.hours.toFixed(2)),
    Pay: Number(row.pay.toFixed(2)),
  }));
}

export default function PayrollSummaryPage() {
  const { role, loading: roleLoading } = useUserRole();
  const { can, loading: permsLoading } = useRolePermissions(role);

  const thisYear = new Date().getFullYear();

  const [year, setYear] = useState(thisYear);

  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);


  const [usersLive, setUsersLive] = useState([]);
  const [entriesLive, setEntriesLive] = useState([]);

  const [roleFilter, setRoleFilter] = useState("All");
  const [positionFilter, setPositionFilter] = useState("All");
  const [thFilter, setThFilter] = useState("All");

  const canAccess = can("TimeCardsRead");

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (roleFilter !== "All" && row.role !== roleFilter) return false;
      if (positionFilter !== "All" && row.position !== positionFilter) return false;
      if (thFilter === "Trinity" && !row.thEmp) return false;
      if (thFilter === "Contractor" && row.thEmp) return false;
      return true;
    });
  }, [rows, roleFilter, positionFilter, thFilter]);

  const roleOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.role).filter(Boolean))).sort()],
    [rows]
  );

  const positionOptions = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.position).filter(Boolean))).sort()],
    [rows]
  );

  const totals = useMemo(() => {
    const totalHours = filteredRows.reduce((sum, row) => sum + row.hours, 0);
    const totalPay = filteredRows.reduce((sum, row) => sum + row.pay, 0);
    return {
      totalHours: Number(totalHours.toFixed(2)),
      totalPay: Number(totalPay.toFixed(2)),
    };
  }, [filteredRows]);

  useEffect(() => {
    if (roleLoading || permsLoading || !canAccess) return;

    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => {
        const nextUsers = snap.docs
          .map((d) => ({ id: d.id, uid: d.id, ...d.data() }))
       .filter((u) => {
  const activeValue = u.active;
  const statusValue = String(u.status || "").toLowerCase();

  if (activeValue === false) return false;
  if (statusValue === "inactive") return false;

  return true;
});

        setUsersLive(nextUsers);

        setRows((prev) => {
          const existingByUid = new Map((prev || []).map((r) => [String(r.uid || "").trim(), r]));
          const base = buildBaseRows(nextUsers);

          const merged = base.map((row) => {
            const existing = existingByUid.get(String(row.uid || "").trim());
            if (!existing) return row;
            return {
              ...row,
              hours: typeof existing.hours === "number" ? existing.hours : 0,
              pay: typeof existing.pay === "number" ? existing.pay : 0,
              rateDisplay: existing.rateDisplay || "",
            };
          });
          return merged;
        });

        setInitialized(true);
      },
      (e) => {
        console.error("Users live listener failed:", e);
        setErr(e?.message || "Could not subscribe to users.");
        setInitialized(true);
      }
    );

    return () => unsub();
  }, [roleLoading, permsLoading, canAccess, year]);

  useEffect(() => {
    if (roleLoading || permsLoading || !canAccess) return;

    const rangeStart = Timestamp.fromDate(getYearStart(year));
    const rangeEnd = Timestamp.fromDate(getYearEnd(year));

    const entriesQ = query(
      collectionGroup(db, "entries"),
      where("startTime", ">=", rangeStart),
      where("startTime", "<=", rangeEnd),
      orderBy("startTime", "asc")
    );

    const unsub = onSnapshot(
      entriesQ,
      (snap) => {
        const nextEntries = snap.docs.map((d) => ({
          id: d.id,
          uid: d.ref.parent.parent?.id || "",
          ...d.data(),
        }));
        setEntriesLive(nextEntries);
      },
      (e) => {
        console.error("Entries live listener failed:", e);
        setErr(e?.message || "Could not subscribe to punches.");
        setInitialized(true);
      }
    );

    return () => unsub();
  }, [year, roleLoading, permsLoading, canAccess]);

  useEffect(() => {
    let cancelled = false;

    async function applyPunchTotals() {
      if (roleLoading || permsLoading || !canAccess) return;
      if (!usersLive.length) {
        setLoading(false);
        setInitialized(true);
        return;
      }

      try {
        setLoading(true);
        setErr("");

        const usersByUid = new Map(
          usersLive.map((u) => [String(u.uid || u.id || "").trim(), u])
        );

        const totalsByUid = new Map();

        for (const entry of entriesLive) {
          const uid = String(entry.uid || "").trim();
          if (!uid) continue;
          if (entry.deleted) continue;
          if (entry.open || !entry.endTime) continue;

          const user = usersByUid.get(uid);
          if (!user) continue;

          const startTime = toDate(entry.startTime);
          const endTime = toDate(entry.endTime);
          if (!startTime || !endTime) continue;

          let payroll;

          if (
            typeof entry.rateUsed === "number" &&
            typeof entry.hoursWorked === "number"
          ) {
            const roundedHours = Number(Number(entry.hoursWorked || 0).toFixed(2));
const roundedRate = Number(Number(entry.rateUsed || 0).toFixed(2));

payroll = {
  rateUsed: roundedRate,
  hoursWorked: roundedHours,
  totalPay:
    typeof entry.totalPay === "number"
      ? Number(entry.totalPay.toFixed(2))
      : Number((roundedHours * roundedRate).toFixed(2)),
};
          } else {
            payroll = await resolvePayrollForPunch({
              uid,
              startTime,
              endTime,
              userData: user,
            });
          }

         const hoursWorked = Number(
  Number(payroll?.hoursWorked || 0).toFixed(2)
);

const rateUsed = Number(
  Number(payroll?.rateUsed || 0).toFixed(2)
);

const totalPayForEntry =
  typeof payroll?.totalPay === "number"
    ? Number(payroll.totalPay.toFixed(2))
    : Number((hoursWorked * rateUsed).toFixed(2));

          if (hoursWorked <= 0) continue;

          if (!totalsByUid.has(uid)) {
            totalsByUid.set(uid, {
              hours: 0,
              pay: 0,
              rates: new Set(),
            });
          }

          const bucket = totalsByUid.get(uid);
          bucket.hours += hoursWorked;
          bucket.pay += totalPayForEntry;
          if (rateUsed > 0) bucket.rates.add(rateUsed.toFixed(2));
        }

        if (cancelled) return;

        setRows((prev) => {
          const base = buildBaseRows(usersLive);

          const merged = base.map((row) => {
            const totals = totalsByUid.get(String(row.uid || "").trim());

            let rateDisplay = "";
            let hours = 0;
            let pay = 0;

            if (totals) {
              hours = Number((totals.hours || 0).toFixed(2));
              pay = Number((totals.pay || 0).toFixed(2));

              if (totals.rates.size === 1) {
                rateDisplay = `$${[...totals.rates][0]}`;
              } else if (totals.rates.size > 1) {
                rateDisplay = "Varies";
              }
            }

            return {
              ...row,
              rateDisplay,
              hours,
              pay,
            };
          });
          return merged;
        });

        setInitialized(true);
      } catch (e) {
        console.error("Payroll live recompute failed:", e);
        if (!cancelled) {
          setErr(e?.message || "Could not build payroll summary.");
          setInitialized(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    applyPunchTotals();

    return () => {
      cancelled = true;
    };
  }, [usersLive, entriesLive, year, roleLoading, permsLoading, canAccess]);

  function handleRefresh() {
  setLoading(true);
  setTimeout(() => setLoading(false), 250);
}

  async function handleDownloadExcel() {
    try {
      setErr("");
      const XLSX = await import("xlsx");
      const exportRows = buildExportRows(filteredRows);
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportRows, {
        header: [
          "Last Name",
          "First Name",
          "E-Mail Address",
          "Supplier ID #",
          "Street",
          "City",
          "State",
          "Zip",
          "TH Emp?",
          "Emp ID",
          "W9",
          "Level",
          "Rate",
          "Hours",
          "Pay",
        ],
      });

      ws["!cols"] = [
        { wch: 18 },
        { wch: 16 },
        { wch: 28 },
        { wch: 14 },
        { wch: 24 },
        { wch: 16 },
        { wch: 8 },
        { wch: 10 },
        { wch: 10 },
        { wch: 12 },
        { wch: 8 },
        { wch: 16 },
        { wch: 12 },
        { wch: 10 },
        { wch: 12 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Payroll");
      XLSX.writeFile(wb, `Payroll_TravelersChampionship_${year}.xlsx`);
    } catch (e) {
      console.error("Excel export failed:", e);
      setErr(e?.message || "Could not export Excel file.");
    }
  }

  function handlePrint() {
    window.print();
  }

  if (roleLoading || permsLoading) {
    return <div style={{ padding: 20 }}>Loading…</div>;
  }

  if (!canAccess) {
    return (
      <div style={{ padding: 20 }}>
        <AdminPageHeader title="Payroll Summary" />
        <p>You do not have access to this page.</p>
      </div>
    );
  }

  const cardStyle = {
    background: "#fff",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 18,
    boxShadow: "0 10px 18px rgba(15, 23, 42, 0.06)",
    padding: 18,
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15, 23, 42, 0.12)",
    boxSizing: "border-box",
    fontSize: 15,
    background: "#fff",
    minHeight: 44,
  };

  return (
    <div style={{ padding: 20 }}>
      <AdminPageHeader title="Payroll Summary" />

      <div style={{ color: "#64748b", marginTop: -8, marginBottom: 14 }}>
        Calendar-year payroll export with Excel download and print summary
      </div>

      {err ? (
        <div
          style={{
            marginBottom: 12,
            padding: "12px 14px",
            borderRadius: 14,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            fontWeight: 700,
          }}
        >
          {err}
        </div>
      ) : null}

      <div style={cardStyle}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            alignItems: "end",
          }}
        >
          <div>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Year</div>
            <input
              type="number"
              min="2024"
              max="2100"
              step="1"
              value={year}
              onChange={(e) => setYear(Number(e.target.value || thisYear))}
              style={inputStyle}
            />
          </div>

          <div>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Role</div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={inputStyle}
            >
              {roleOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Position</div>
            <select
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
              style={inputStyle}
            >
              {positionOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Employment Type</div>
            <select
              value={thFilter}
              onChange={(e) => setThFilter(e.target.value)}
              style={inputStyle}
            >
              <option value="All">All</option>
              <option value="Trinity">Trinity Health Employee</option>
              <option value="Contractor">Contractor</option>
            </select>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginTop: 14,
          }}
        >
          <button className="btn btn-primary" onClick={handleRefresh} disabled={loading}>
            {loading ? "Updating…" : "Refresh Payroll"}
          </button>

          <button
            className="btn"
            onClick={handleDownloadExcel}
            disabled={filteredRows.length === 0}
          >
            Download Excel
          </button>

          <button
            className="btn"
            onClick={handlePrint}
            disabled={filteredRows.length === 0}
          >
            Print Summary
          </button>
        </div>
      </div>

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 900, fontSize: 18 }}>
            Payroll Preview
          </div>

          <div style={{ color: "#64748b", fontWeight: 700 }}>
            {filteredRows.length} employee{filteredRows.length === 1 ? "" : "s"}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 14,
            }}
          >
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                {[
                  "Last Name",
                  "First Name",
                  "E-Mail Address",
                  "Supplier ID #",
                  "Street",
                  "City",
                  "State",
                  "Zip",
                  "TH Emp?",
                  "Emp ID",
                  "W9",
                  "Level",
                  "Rate",
                  "Hours",
                  "Pay",
                ].map((label) => (
                  <th
                    key={label}
                    style={{
                      textAlign: "left",
                      padding: "10px 8px",
                      borderBottom: "1px solid #e2e8f0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={15} style={{ padding: 16, color: "#64748b" }}>
                    {!initialized
                      ? "Loading payroll…"
                      : loading
                      ? "Updating…"
                      : "No rows to display."}
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.uid}>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.lastName}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.firstName}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.email}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }} />
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.street}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.city}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.state}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.zip}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.thEmp ? "Yes" : "No"}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.empId}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }} />
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.level}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.rateDisplay}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {row.hours.toFixed(2)}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      ${row.pay.toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {filteredRows.length > 0 && (
              <tfoot>
                <tr style={{ background: "#f8fafc", fontWeight: 900 }}>
                  <td colSpan={13} style={{ padding: "12px 8px", borderTop: "2px solid #e2e8f0" }}>
                    Totals
                  </td>
                  <td style={{ padding: "12px 8px", borderTop: "2px solid #e2e8f0" }}>
                    {totals.totalHours.toFixed(2)}
                  </td>
                  <td style={{ padding: "12px 8px", borderTop: "2px solid #e2e8f0" }}>
                    ${totals.totalPay.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}